import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import defaultFormState, { requiredFields } from '@models/agencyProvider'

import PageContext from '@contexts/pageContext'

import * as agencyProviderActions from '@redux/modules/agencyProvider'
import type { AppDispatch } from '@redux/store'
import type { DeleteParams } from '@redux/modules/types'

import type { AgencyProviderModel, AgencyProviderRequestOptions } from '@models/types'

type CreateAgencyProviderParams = {
  agencyProviderParams: Partial<AgencyProviderModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyProviderRequestOptions,
}

const createAgencyProvider = (params: CreateAgencyProviderParams) => {
  const { dispatch, agencyProviderParams, requestOptions } = params
  const { createAgencyProvider: createFn } = agencyProviderActions

  return dispatch(createFn(agencyProviderParams, requestOptions))
}

type UpdateAgencyProviderParams = {
  agencyProvider: AgencyProviderModel,
  agencyProviderParams: Partial<AgencyProviderModel>,
  dispatch: AppDispatch,
  requestOptions?: AgencyProviderRequestOptions,
}

const updateAgencyProvider = (params: UpdateAgencyProviderParams) => {
  const {
    dispatch, agencyProvider, agencyProviderParams, requestOptions,
  } = params
  const { updateAgencyProvider: updateFn } = agencyProviderActions

  const updatedParams = {
    id: agencyProvider.id,
    ...agencyProviderParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteAgencyProviderParams = {
  agencyProvider: DeleteParams<AgencyProviderModel>,
  dispatch: AppDispatch,
}

const deleteAgencyProvider = (params: DeleteAgencyProviderParams) => {
  const { dispatch, agencyProvider } = params
  const { deleteAgencyProvider: deleteFn } = agencyProviderActions

  return dispatch(deleteFn(agencyProvider))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAgencyProviderForm(
  agencyProvider: Partial<AgencyProviderModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const agencyProviderForm = useForm(
    defaultFormState,
    { entity: agencyProvider, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [agencyProvider.id, agencyProvider.cache_key],
  )

  return {
    ...agencyProviderForm,
  }
}

export const useRelations = (agencyProvider: Partial<AgencyProviderModel> = {}) => {
  const {
    agency_id, provider_id, provider_price_list_id, service_id,
  } = agencyProvider || {}

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    agencies, providers, providerPriceLists, services,
  } = entities

  const agency = agency_id ? agencies[agency_id] || {} : {}
  const provider = provider_id ? providers[provider_id] || {} : {}
  const providerPriceList = provider_price_list_id ? providerPriceLists[provider_price_list_id] || {} : {}
  const service = service_id ? services[service_id] || {} : {}

  return {
    agency,
    provider,
    providerPriceList,
    service,
  }
}

function useAgencyProvider(initEntity: Partial<AgencyProviderModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: agencyProvider }: { entity: AgencyProviderModel } = useLatestEntity(initEntity, 'agencyProviders')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const {
    agency, provider, providerPriceList, service,
  } = useRelations(agencyProvider)

  const {
    creating, deleting, loading, updating,
  } = useSelector(reduxState => reduxState.agencyProviders)

  return {
    agency,
    agencyProvider,
    callbacks: {
      createAgencyProvider: (
        agencyProviderParams: Partial<AgencyProviderModel>,
        entityOptions?: AgencyProviderRequestOptions,
      ) => (
        createAgencyProvider({ agencyProviderParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditAgencyProvider: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditAgencyProviderModal',
        payload: { agencyProvider },
      }),
      deleteAgencyProvider: () => deleteAgencyProvider({ dispatch, agencyProvider }),
      updateAgencyProvider: (
        agencyProviderParams: Partial<AgencyProviderModel>,
        entityOptions?: AgencyProviderRequestOptions,
      ) => (
        updateAgencyProvider({
          agencyProvider, agencyProviderParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    loading,
    provider,
    providerPriceList,
    service,
    updating,
  }
}

export default useAgencyProvider
